export const PILLARS = [
  {
    id: "mental-health",
    number: "01",
    title: "Mental Health",
    hindi: "मानस",
    description:
      "Honest conversations about the mind — anxiety, rest, resilience and the quiet work of coming back to yourself.",
    span: "md:col-span-7",
    tone: "lavender",
  },
  {
    id: "womens-health",
    number: "02",
    title: "Women's Health & Well-being",
    hindi: "शक्ति",
    description:
      "Cycles, seasons, strength. A space where women's bodies and stories are heard without judgement.",
    span: "md:col-span-5",
    tone: "peach",
  },
  {
    id: "conscious-parenting",
    number: "03",
    title: "Children & Conscious Parenting",
    hindi: "संस्कार",
    description:
      "Raising children with presence — gentle discipline, emotional literacy and wonder kept alive.",
    span: "md:col-span-5",
    tone: "peach",
  },
  {
    id: "weekly-column",
    number: "04",
    title: "Samatva Weekly",
    hindi: "तत्त्वबोध",
    description:
      "One reflective essay, every week. Slow reading for fast lives — on balance, habit and meaning.",
    span: "md:col-span-7",
    tone: "lavender",
  },
  {
    id: "vaarta",
    number: "05",
    title: "Vaarta — Stories & Podcast",
    hindi: "वार्ता",
    description:
      "Vaarta means conversation. Real voices from the community — told, heard and kept.",
    span: "md:col-span-6",
    tone: "lavender",
  },
  {
    id: "food",
    number: "06",
    title: "Food Beyond Nourishment",
    hindi: "आहार",
    description:
      "The kitchen as the first pharmacy. Seasonal recipes, mindful eating and food as memory.",
    span: "md:col-span-6",
    tone: "peach",
  },
  {
    id: "spirituality",
    number: "07",
    title: "Spirituality & Indian Wisdom",
    hindi: "",
    description:
      "Not religion — relationship. With breath, with silence, with whatever you call the sacred.",
    span: "md:col-span-8",
    tone: "peach",
  },
];

export const MANIFESTO = [
  {
    number: "I",
    title: "Health is an umbrella",
    body: "Health is not the absence of illness. It is the presence of balance — in how we eat, sleep, love, grieve, parent and pray. Samatva holds the whole umbrella, not one spoke of it.",
  },
  {
    number: "II",
    title: "Balance is a practice",
    body: "Samatva — समत्व — is the Sanskrit word for equanimity. The Gita says samatvam yoga uchyate: equanimity itself is yoga. Not a destination, but a daily returning.",
  },
  {
    number: "III",
    title: "Community is medicine",
    body: "No one heals alone. Through the weekly column, Vaarta stories and shared rituals, we sit beside each other — and remember that wholeness was never lost, only forgotten.",
  },
];

export const COLUMN_ARTICLES = [
  {
    week: "Week 28 · July 2026",
    title: "The Art of Doing Nothing, Well",
    excerpt:
      "Rest is not a reward for exhaustion. On Niksen, the lost skill of idleness, and why your nervous system is begging you to stare out of the window.",
    tag: "Mental Health",
  },
  {
    week: "Week 27 · July 2026",
    title: "What My Grandmother's Kitchen Knew",
    excerpt:
      "Before nutrition labels there was nose and hand and season. A love letter to intuitive cooking, and the ayurvedic logic hidden inside every tadka.",
    tag: "Food & Ayurveda",
  },
  {
    week: "Week 26 · July 2026",
    title: "Parenting the Parent",
    excerpt:
      "Our children do not need perfect parents; they need regulated ones. On repairing after rupture, and the courage of saying 'I am sorry' to a six-year-old.",
    tag: "Conscious Parenting",
  },
  {
    week: "Week 25 · June 2026",
    title: "Her Cycle Is Not an Inconvenience",
    excerpt:
      "Menstrual health is health. On cycle literacy, rest as rhythm, and why workplaces and homes both need to learn a new calendar.",
    tag: "Women's Health",
  },
];

export const EPISODES = [
  {
    kind: "youtube",
    title: "Vaarta Ep. 12 — On Grief and Morning Tea",
    guest: "A conversation with a bereavement counsellor",
    embedUrl: "https://www.youtube.com/embed/inpok4MKVLM",
  },
  {
    kind: "spotify",
    title: "Vaarta Ep. 11 — Raising Soft Boys",
    guest: "A father unlearning everything",
    embedUrl:
      "https://open.spotify.com/embed/show/5CfCWKI5pZ28U0uOzXkDHe?utm_source=generator",
  },
];

export const IMAGES = {
  hero: "https://images.pexels.com/photos/32585401/pexels-photo-32585401.jpeg",
  vaarta:
    "https://images.unsplash.com/photo-1526779259212-939e64788e3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxwZWFjZWZ1bCUyMG1lZGl0YXRpb24lMjBuYXR1cmV8ZW58MHx8fHwxNzg3MjQ3MTYzfDA&ixlib=rb-4.1.0&q=85",
  food: "/samatva-join-circle.png",
  tea: "/samatva-weekly.png",
};

export const ARTICLES = [
  {
    slug: "is-every-battle-worth-fighting",
    week: "Week 01",
    tag: "Spirituality & Indian Wisdom",
    title: "Is Every Battle Worth Fighting?",
    subtitle:
      "What Krishna's Teachings Reveal About Dharma, Courage and the Wisdom of Walking Away",
    author: "Rashmi Parmar",
    excerpt:
      "We are taught to associate courage with confrontation and retreat with defeat. But does Dharma always require us to fight — or can walking away also be an act of wisdom?",
    sections: [
      {
        heading: null,
        paragraphs: [
          "There is a certain conditioning that many of us grow up with: never give up, always stand your ground, and fight for what is right. We are taught to associate courage with confrontation and retreat with defeat.",
          "But as we navigate relationships, responsibilities and the complexities of everyday life, one of the fundamental questions that arises is: Does Dharma always require us to fight, or can walking away also be an act of wisdom?",
          "The teachings and stories associated with Lord Krishna offer a nuanced perspective on this question.",
        ],
      },
      {
        heading: "The Battlefield of Kurukshetra: When Duty Demands Action",
        paragraphs: [
          "In the Bhagavad Gita, Arjuna stands on the battlefield of Kurukshetra, overwhelmed by the prospect of fighting his own relatives, teachers and the loved ones. He questions the purpose of war and wonders whether victory is worth the destruction it may bring. Arjuna wishes to withdraw.",
          "Krishna, however, does not simply ask him to follow his emotions. Instead, He encourages Arjuna to examine his responsibilities, his attachments and the nature of Dharma. Through His teachings, Krishna explains that abandoning one's rightful duty merely because it is difficult or emotionally painful may not constitute true renunciation.",
          "The message is significant: Avoiding a necessary responsibility is not always an act of peace. Sometimes, it is an escape from discomfort. There are moments when we must stand up for justice, protect those who are vulnerable and fulfil responsibilities that cannot simply be abandoned. However, Krishna's teachings also emphasise the importance of acting without attachment to the fruits of one's actions. The objective is not to fight for the sake of victory, but to act with clarity and integrity.",
        ],
      },
      {
        heading: "The Need to Fight Every Battle",
        paragraphs: [
          "Dealing with difficult people can be emotionally exhausting. Often, the most exhausting part is not even their behaviour, but it is our persistent desire to make them understand. We tend to explain ourselves, defend our intentions, revisit conversations, and wait for an apology. We hope that, eventually, they will recognise the hurt they have caused. But when that recognition does not come, we find ourselves returning to the same emotional battlefield. Perhaps there is a question worth asking:",
          "Am I trying to resolve the situation, or am I trying to obtain validation from someone who may never offer it?",
        ],
      },
      {
        heading: "Krishna's Departure from Mathura: When Walking Away Becomes Wisdom",
        paragraphs: [
          "The story of Krishna's departure from Mathura from the Bhagwat Puran offers an interesting contrast to the events of Kurukshetra. According to the Srimad Bhagavatam, Krishna and Balarama faced repeated attacks from Jarasandha, the king of Magadha. Krishna subsequently moved away from Mathura and established His people in Dvaraka.",
          "In devotional tradition, Krishna is also revered as Ranchhodrai—the one who left the battlefield. At first glance, this may appear to contradict His message to Arjuna. If Dharma requires courage, why would Krishna choose to leave? The answer lies in understanding that courage does not always express itself through confrontation. Krishna's actions can be understood as an example of strategic decision-making, in which the protection of His people took precedence over the pursuit of a direct confrontation. He did not abandon His responsibility. He chose a different way to fulfil it.",
          "This story invites us to reconsider our own understanding of retreat. Is leaving always a sign of weakness? What if stepping away from a conflict protects something more valuable? What if refusing to participate in a destructive argument preserves a relationship? What if leaving an environment that repeatedly compromises our values allows us to rebuild our lives with greater clarity?",
          "Not every retreat is a defeat. Sometimes, it is a conscious redirection of the effort.",
        ],
      },
      {
        heading: "Fighting for Dharma or Fighting for the Ego?",
        paragraphs: [
          "One of the most difficult distinctions in life is recognising whether we are fighting for what is right or merely trying to prove ourselves right. A disagreement may begin with a genuine concern, but it can gradually become a struggle for recognition and acknowledgement. We may want an apology, an acknowledgement of our pain or an admission that the other person was wrong.",
          "When that acknowledgement does not come, we continue the argument, hoping that one more explanation will change the outcome. But does our truth become less valid because someone refuses to accept it? And does every disagreement require our intervention?",
          "The Bhagavad Gita encourages us to examine the intention behind our actions. This is particularly relevant to our emotional lives. A confrontation may appear courageous but be driven by anger. Conversely, stepping away may reflect thoughtful restraint rather than fear.",
          "The distinction lies not merely in what we do, but in why we do it. Walking away to avoid accountability is different from walking away to protect one's peace and dignity. Standing up for justice is different from fighting to satisfy one's ego. Recognising this difference requires self-awareness.",
        ],
      },
      {
        heading: "When Leaving Is Not the Right Choice",
        paragraphs: [
          "The idea of walking away must also be approached with caution. There are situations in which withdrawal may mean abandoning a responsibility. A parent cannot avoid every difficult conversation with a child. A person cannot ignore injustice simply because confronting it is uncomfortable. A friend may have a responsibility to support someone going through a difficult time. Similarly, maintaining peace at all costs can sometimes mean tolerating behaviour that should be addressed.",
          "Dharma is not a universal formula that demands the same response in every situation. Our responsibilities depend on the circumstances, our relationships and the consequences of our actions. Sometimes, Dharma requires us to speak. Sometimes, it requires us to listen. Sometimes, it calls for a firm boundary. And sometimes, it may require us to leave. The challenge is to distinguish between a thoughtful decision and a convenient escape.",
        ],
      },
      {
        heading: "The Wisdom of Choosing Our Battles",
        paragraphs: [
          "In a world where constant assertion is often mistaken for strength, there is value in learning when not to engage.",
          "We do not have to respond to every provocation. We do not have to explain every decision. We do not have to win every argument. Nor do we have to remain silent when our voice could prevent harm or protect someone in need.",
        ],
      },
    ],
  },
];

export const CATEGORIES = [
  { id: "mental-health", title: "Mental Health", hindi: "मानस" },
  { id: "womens-health", title: "Women's Health & Well-being", hindi: "शक्ति" },
  {
    id: "conscious-parenting",
    title: "Children & Conscious Parenting",
    hindi: "संस्कार",
  },
  { id: "weekly-column", title: "Samatva Weekly", hindi: "तत्त्वबोध" },
  { id: "vaarta", title: "Vaarta — Stories & Podcast", hindi: "वार्ता" },
  { id: "food", title: "Food Beyond Nourishment", hindi: "आहार" },
  { id: "ayurveda", title: "Ayurveda", hindi: "आयुर्वेद" },
  { id: "spirituality", title: "Spirituality & Indian Wisdom", hindi: "" },
];
